import { useQuery } from '@apollo/client'
import { useLoginInfo } from 'app/_hooks'
import { useParams } from 'next/navigation'
import { useMemo } from 'react'
import { usePlaylistActions } from 'app/_hooks/usePlaylistActions'
import {
  GET_PLAYLISTS,
  GET_VIDEO_PLAYLISTS,
} from '~/features/play_list/graphql/query/getPlaylistRegistration'

export interface PlayListInfo {
  playListId: number
  playListVideoId: string | undefined
  title: string
  isRegisteredPlayList: boolean
}

export interface PlayListRegistrationInfo {
  allPlayListInfo: PlayListInfo[]
  /** プレイリストID */
  playListId: number | undefined
  /** プレイリスト動画ID */
  playListVideoId: string | undefined
}

/**
 * アクション対象の動画に関わるプレイリスト情報をまとめるhooks
 */
export const usePlayListRegistrationInfo = (): PlayListRegistrationInfo | 'loading' => {
  const params = useParams()
  const playListQueryId = params ? Number(params.id) : undefined
  const loginInfo = useLoginInfo()
  const { targetActionPlayListVideoInfo } = usePlaylistActions()

  const videoId = targetActionPlayListVideoInfo?.videoId
  const signedIn = loginInfo.status === 'signedIn'

  const { data: playlistsData } = useQuery(GET_PLAYLISTS, {
    skip: !signedIn,
  })

  const { data: videoData } = useQuery(GET_VIDEO_PLAYLISTS, {
    variables: { videoId: String(videoId) },
    skip: !signedIn || videoId === undefined,
  })

  return useMemo(() => {
    if (
      !playlistsData ||
      !videoData ||
      videoId === undefined ||
      targetActionPlayListVideoInfo === undefined
    ) {
      return 'loading'
    }

    const playlists = playlistsData.playlists ?? []
    const playlistsVideos = videoData.originalVideo?.playlistsVideos ?? []

    // Map playlistId -> playlistsVideoId
    const registeredMap = new Map<string, string>()
    for (const pv of playlistsVideos) {
      registeredMap.set(pv.playlist.id, pv.id)
    }

    const allPlayListInfo: PlayListInfo[] = playlists.map((p) => ({
      playListId: Number(p.id),
      title: p.title ?? '',
      isRegisteredPlayList: registeredMap.has(p.id),
      playListVideoId: registeredMap.get(p.id),
    }))

    const getPlayListVideoId = () => {
      if (playListQueryId === undefined) return undefined
      const queryIdStr = String(playListQueryId)
      const entry = playlistsVideos.find(
        (pv) => pv.playlist.id === queryIdStr,
      )
      return entry?.id
    }

    return {
      playListId: playListQueryId,
      allPlayListInfo,
      playListVideoId: getPlayListVideoId(),
    }
  }, [playlistsData, videoData, videoId, playListQueryId, targetActionPlayListVideoInfo])
}
